import type { CSSProperties } from "react";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import type { LessonWorkspaceMode } from "../auth/api-client";
import { fallbackGradient, lessonIconDefinition } from "./lessonIcons";
import { lessonEpicVisualStyle } from "./lessonAdvancedVisual";
import { textColorsForBackground } from "./lessonCardColors";

/** Translucent button fills keyed off the contrasting text tone (cross-browser, no color-mix). */
const BUTTON_FILLS = {
  dark: { base: "rgba(255, 255, 255, 0.16)", hover: "rgba(255, 255, 255, 0.28)" },
  light: { base: "rgba(0, 0, 0, 0.08)", hover: "rgba(0, 0, 0, 0.16)" },
} as const;

export interface LessonImmersiveTheme {
  readonly background: string;
  readonly fg: string;
  readonly muted: string;
  readonly iconDef: IconDefinition | null;
  readonly watermarkColor: string;
  readonly style: CSSProperties;
}

export interface LessonImmersiveThemeInput {
  readonly iconName?: string | null;
  readonly iconBgColor?: string | null;
  readonly iconFgColor?: string | null;
  readonly workspaceMode?: LessonWorkspaceMode | null;
  readonly seed: string;
}

/**
 * Builds the immersive page theme for a lesson workspace: a full-bleed gradient
 * background derived from the lesson's color scheme, an accessible contrasting
 * text color, and the lesson icon to use as a large transparent watermark.
 */
export function buildLessonImmersiveTheme(input: LessonImmersiveThemeInput): LessonImmersiveTheme {
  const iconDef = lessonIconDefinition(input.iconName);
  const baseColor = input.iconBgColor?.trim() || "";
  const epic = lessonEpicVisualStyle({
    workspaceMode: input.workspaceMode,
    baseColor: baseColor || undefined,
    fallbackSeed: input.seed,
  });
  const background =
    epic?.innerBackground ?? (baseColor || (iconDef ? "#1b4d2e" : fallbackGradient(input.seed)));
  const colors = textColorsForBackground(background);
  const watermarkColor = input.iconFgColor?.trim() || colors.title;
  const buttonFill = BUTTON_FILLS[colors.tone];

  const style: CSSProperties = {
    "--ide-immersive-bg": background,
    "--ide-immersive-fg": colors.title,
    "--ide-immersive-muted": colors.blurb,
    "--ide-immersive-watermark": watermarkColor,
    "--ide-immersive-btn-bg": buttonFill.base,
    "--ide-immersive-btn-bg-hover": buttonFill.hover,
  } as CSSProperties;

  return {
    background,
    fg: colors.title,
    muted: colors.blurb,
    iconDef,
    watermarkColor,
    style,
  };
}
